import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import { ANALYSIS_RESULT, STATE_COLOR, BATTERY_TYPE, formatDateTime, formatDate } from './enums';
import './locations_table.css';

// Material UI
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarExport,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { styled } from '@mui/material/styles';

function buildCell(text, alignment, blankText = '--', cellColor = null) {

	let displayText = (text !== undefined && text !== null && text !== 'null' && text !== 'null %' && text !== 'null years') ? text : blankText;

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: alignment,
				backgroundColor: cellColor ? cellColor : 'none',
				border: cellColor ? '1px solid #666666' : 'none',
			}}
		>
			{displayText}
		</div>
	);
}

// Locations List table columns
const columns = [
	{
		field: 'overall_system_analysis',
		headerName: 'Analysis',
		headerAlign: "left",
		headerClassName: 'hidden-header' ,
		description: 'Overall System Analysis status',
		minWidth: 65,
		flex: 1,
		filterable: false,
		sortable: false,
		renderCell: (params) => {
			const status = params.value;

			let icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
			let tooltip = "Unknown Status";

			if (
				status === ANALYSIS_RESULT.ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT ||
				status === ANALYSIS_RESULT.ANALYSIS_OUT_OF_RANGE_CRITICAL ||
				status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL ||
				status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL
			){
				icon = <ErrorOutlineIcon style={{ color: 'black' }} />;
				tooltip = "Critical Analysis";
			} else if (
				status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS ||
				status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS
			) {
				icon = <WarningAmberOutlinedIcon style={{ color: STATE_COLOR.MAINTENANCE }} />;
				tooltip = "Maintenance Analysis";
			} else if (
				status === ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS
			) {
				icon = <CheckCircleOutlineOutlinedIcon style={{ color: STATE_COLOR.WITHIN_LIMITS }} />;
				tooltip = "Within Limits";
			} else {
				icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
				tooltip = "Unknown Status";
			}

			return <Tooltip title={tooltip} placement="top">{icon}</Tooltip>;
		},
	},
	{
		field: 'location_name',
		headerName: 'Name',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Location Name',
		minWidth: 250,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'serial_number',
		headerName: 'Serial #',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Serial Number',
		minWidth: 75,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left', ),
	},
	{
		field: 'bvs_model',
		headerName: 'Model',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'BVS Controller Model',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center'),
	},
	{
		field: 'organization',
		headerName: 'Organization',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Organization Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'region',
		headerName: 'Region',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Region Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'facility',
		headerName: 'Facility',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Facility Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'building',
		headerName: 'Building',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'Building Hierarchy',
		minWidth: 150,
		flex: 1,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'num_strings',
		headerName: '# Strings',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Strings in the System',
		minWidth: 75,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center'),
	},
	{
		field: 'num_units_per_string',
		headerName: 'Units per String',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units per String in the System',
		minWidth: 75,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center'),
	},
	{
		field: 'temp_sensors',
		headerName: '# Temp Sensors',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Temperature Sensors in the System',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center'),
	},
	{
		field: 'num_units_crit',
		headerName: '# Units Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units exceeding Critical Limits in the System',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_units_maint',
		headerName: '# Units Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'Number of Units past Maintenance Limits in the System',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.MAINTENANCE : null),
	},
	{
		field: 'batt_type',
		headerName: 'Battery Type',
		headerAlign: "left",
		headerClassName: 'wrap-header-text',
		description: 'Type of Battery',
		minWidth: 150,
		flex: 1,
		sortable: false,
		filterable: false,
		valueGetter: (params) => BATTERY_TYPE[params.value],
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'batt_model',
		headerName: 'Battery Model',
		headerAlign: "left",
		headerClassName: 'wrap-header-text' ,
		description: 'The exact battery model',
		minWidth: 165,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'left'),
	},
	{
		field: 'controller_ip',
		headerName: 'IP',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s IP address',
		minWidth: 130,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'right', '--.--.--.--'),
	},
	{
		field: 'network_port',
		headerName: 'Port',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s IP Port number',
		minWidth: 65,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '----'),
	},
	{
		field: 'controller_gateway',
		headerName: 'Gateway',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s Gateway IP Address',
		minWidth: 130,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--.--.--.--'),
	},
	{
		field: 'controller_mac',
		headerName: 'MAC Address',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The BVS Controller\'s MAC Address',
		minWidth: 175,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--:--:--:--:--:--'),
	},
	{
		field: 'poll_interval',
		headerName: 'Poll Interval',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The interval that Observer polls the BVS Controller',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'right', '--:--:--'),
	},
	{
		field: 'last_poll',
		headerName: 'Last Poll Date',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time that Observer last polled the BVS Controller',
		minWidth: 165,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDateTime(params.value), 'right'),
	},
	{
		field: 'most_recent_measurement',
		headerName: 'Last Measurement',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the most recent measurement',
		minWidth: 165,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDateTime(params.value), 'right'),
	},
	{
		field: 'most_recent_event',
		headerName: 'Most Recent Event',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the most recent alarm',
		minWidth: 165,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDateTime(params.value), 'right'),
	},
	{
		field: 'num_recent_events',
		headerName: '# Recent Events',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of alarms since the previous measurement',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '---'),
	},
	{
		field: 'num_recent_discharges',
		headerName: '# Recent Discharges',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of discharge events since the previous measurement',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '---'),
	},
	{
		field: 'previous_measurement',
		headerName: 'Previous Measurement',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date and time of the previous measurement',
		minWidth: 165,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDateTime(params.value), 'right'),
	},
	{
		field: 'risk_index',
		headerName: 'Risk Index',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The overall Risk Index of the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(
			params.value,
			(params.value === 'Inoperable') ? 'center' : 'right',
			'--',
			(params.value === 'Inoperable') ? STATE_COLOR.CRITICAL : null
		),
	},
	{
		field: 'num_strings_missing_meas',
		headerName: '# Strings Missing Meas',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of strings in the system that are missing measurements',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_volt_upper_crit',
		headerName: '# Unit Volt Upper Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages exceeding Upper Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_volt_upper_maint',
		headerName: '# Unit Volt Upper Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages exceeding Upper Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.MAINTENANCE : null),
	},
	{
		field: 'num_volt_lower_maint',
		headerName: '# Unit Volt Lower Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Lower Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.MAINTENANCE : null),
	},
	{
		field: 'num_volt_lower_crit',
		headerName: '# Unit Volt Lower Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Lower Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_discharge_crit',
		headerName: '# Unit Crit Discharge',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Critical Limits during a recent discharge event',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_discharge_maint',
		headerName: '# Unit Maint Discharge',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with voltages below Maintenance Limits during a recent discharge event',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.MAINTENANCE : null),
	},
	{
		field: 'num_imped_crit',
		headerName: '# Unit Imped Crit',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with impedances exceeding Critical Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.CRITICAL : null),
	},
	{
		field: 'num_imped_maint',
		headerName: '# Unit Imped Maint',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units with impedances exceeding Maintenance Limits',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'center', '--', (params.value > 0) ? STATE_COLOR.MAINTENANCE : null),
	},
	{
		field: 'avg_imped_increase',
		headerName: 'Avg Imped Inc %',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average impedance increase percentage across the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value + " %", 'right', '--.-- %'),
	},
	{
		field: 'avg_unit_in_service',
		headerName: 'Avg Unit Install Age',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average number of years that a unit has been installed in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value + " years", 'right', '--.-- years'),
	},
	{
		field: 'oldest_installation_date',
		headerName: 'Oldest Unit Install',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The date of the unit that has been installed in the system the longest',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDate(params.value, '--/--/----'), 'right'),
	},
	{
		field: 'avg_mfg_age',
		headerName: 'Avg Unit Mfg Age',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The average manufacture age of units in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value + " years", 'right', '--.-- years'),
	},
	{
		field: 'oldest_mfg_date',
		headerName: 'Oldest Unit Mfg',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The oldest manufacture age of a unit in the system',
		minWidth: 120,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(formatDate(params.value, '--/--/----'), 'right'),
	},
	{
		field: 'num_units_replaced',
		headerName: '# Units Replaced',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The number of units replaced in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'right', '--'),
	},
	{
		field: 'percent_units_replaced',
		headerName: '% Units Replaced',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The percentage of units replaced in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value + " %", 'right', '--.-- %'),
	},
	{
		field: 'num_units_out_of_warranty',
		headerName: '# Units Past Warranty',
		headerAlign: "center",
		headerClassName: 'wrap-header-text' ,
		description: 'The total number of units that are past their warranty date in the system',
		minWidth: 100,
		flex: 1,
		sortable: false,
		filterable: false,
		renderCell: (params) => buildCell(params.value, 'right', '--'),
	},
];

function LocationListToolbar() {
	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
			<GridToolbarDensitySelector />
			<GridToolbarExport />
		</GridToolbarContainer>
	);
}

const StyledGridOverlay = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	'& .ant-empty-img-1': {
		fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
	},
	'& .ant-empty-img-2': {
		fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
	},
	'& .ant-empty-img-3': {
		fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
	},
	'& .ant-empty-img-4': {
		fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
	},
	'& .ant-empty-img-5': {
		fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
		fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
	},
}));

function NoLocationsOverlay() {
	return (
		<StyledGridOverlay>
			<svg
				width="120"
				height="100"
				viewBox="0 0 184 152"
				aria-hidden
				focusable="false"
			>
				<g fill="none" fillRule="evenodd">
					<g transform="translate(24 31.67)">
						<ellipse
							className="ant-empty-img-5"
							cx="67.797"
							cy="106.89"
							rx="67.797"
							ry="12.668"
						/>
						<path
							className="ant-empty-img-1"
							d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
						/>
						<path
							className="ant-empty-img-2"
							d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
						/>
						<path
							className="ant-empty-img-3"
							d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
						/>
					</g>
					<path
						className="ant-empty-img-3"
						d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
					/>
					<g className="ant-empty-img-4" transform="translate(149.65 15.383)">
						<ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
						<path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
					</g>
				</g>
			</svg>
			<Box sx={{ mt: 1 }}>No Locations</Box>
		</StyledGridOverlay>
	);
}

export default function LocationsTable() {

	const [loading, setLoading] = useState(true);
	const [locations, setLocations] = useState([]);
	const [rowCount, setRowCount] = useState(0);

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sortModel, setSortModel] = useState([]);
	const [filterModel, setFilterModel] = useState({ items: [] });

	const navigate = useNavigate();

	const fetchLocations = useCallback(() => {
		setLoading(true);

		const sort = sortModel[0] || {};
		const filter = filterModel.items[0] || {};

		axiosInstance.get('/', {
			params: {
				page: page + 1, // backend pages usually 1-based
				page_size: pageSize,
				sort_field: sort.field,
				sort_order: sort.sort,
				filter_field: filter.columnField,
				filter_value: filter.value,
			}
		})
		.then(res => {
			const locData = res.data.locations || [];
			setLocations(locData);
			setRowCount(res.data.count || 0);
		})
		.catch(error => {
			console.error("Error fetching locations:", error);
			setLocations([]);
			setRowCount(0);
		})
		.finally(() => setLoading(false));
	}, [page, pageSize, sortModel, filterModel]);

	useEffect(() => {
		fetchLocations();
	}, [fetchLocations]);

	function AnalysisResultToStyle(analysisResult) {
		if (analysisResult) {
			switch (analysisResult) {
				case 0:  // ANALYSIS_UNKNOWN
				return 'unknown-color';
	
				case 1:  // ANALYSIS_WITHIN_LIMITS
					return 'withinLimits-color';
	
				case 2:  // ANALYSIS_ABOVE_LIMITS
					return 'maintenance-color';
	
				case 3:  // ANALYSIS_BELOW_LIMITS
					return 'maintenance-color';
	
				case 4:  // ANALYSIS_ABOVE_LIMITS_CRITICAL
					return 'critical-color';
	
				case 5:  // ANALYSIS_BELOW_LIMITS_CRITICAL
					return 'critical-color';
	
				case 6:  // ANALYSIS_OUT_OF_RANGE_CRITICAL
					return 'critical-color';
	
				case 7:  // ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT
					return 'critical-color';
	
				default:
					return 'unknown-color';
			}
		} else {
			return 'unknown-color';
		}
	}

	return (
		<DataGrid
			getRowId={(row) => row.location_id}
			rows={locations}
			columns={columns}
			loading={loading}
			rowCount={rowCount}
			page={page}
			pageSize={pageSize}
			pagination
			paginationMode="server"
			onPageChange={setPage}
			onPageSizeChange={setPageSize}
			sortModel={sortModel}
			sortingMode="server"
			onSortModelChange={setSortModel}
			filterModel={filterModel}
			filterMode="server"
			onFilterModelChange={setFilterModel}
			density='compact'
			onRowClick={(rowParams) => {
				const locationId = rowParams.id;
				navigate(`/location/${locationId}`);
			}}
			getRowClassName={(row) => {
				return AnalysisResultToStyle(row.row.overall_system_analysis)
			}}
			components={{
				Toolbar: LocationListToolbar,
				NoRowsOverlay: NoLocationsOverlay,
			}}
			sx={{
				height: '100%',
				padding: 2,
			}}
		/>
	);
}